import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { PolicyData, withPolicyContext } from '../../context/policy';
import { PremiumReportResponse } from '../../types/premiumReports';
import { formatCurrency } from '../../util/util';
import { PaddedDiv, StyledTitleBolded } from '../common';

interface Props {
	premiumReport: PremiumReportResponse;
	policyData: PolicyData;
}

function PricePreviewTables(props: Props) {
	const { premiumReport, policyData } = props;

	const {
		fees,
		commissions,
		taxGroups,
		premiums,
		grossCommissions,
		grossFees,
		grossPremium,
		grossTaxes,
	} = premiumReport;

	const currency =
		policyData && policyData.policy ? policyData.policy.currency : null;
	const totalPremium = parseFloat(grossPremium);
	const totalFees = parseFloat(grossFees);
	const totalTaxes = parseFloat(grossTaxes);
	const totalCommissions = parseFloat(grossCommissions);

	const totalDue = totalPremium + totalFees + totalTaxes;

	const renderPremiums = () => {
		if (premiums && premiums.length > 0) {
			const premiumRows = premiums.map((prem, index) => {
				const premiumTitle = `Premium ${index + 1}: ${prem.category}`;
				return (
					<TableRow key={`premium-${index}`}>
						<TableCell>{premiumTitle}</TableCell>
						<TableCell align='right'>
							{formatCurrency(prem.premium, currency)}
						</TableCell>
					</TableRow>
				);
			});
			premiumRows.push(
				<TableRow key='premium-total'>
					<TableCell>Total Premium</TableCell>
					<TableCell align='right'>
						{formatCurrency(totalPremium, currency)}
					</TableCell>
				</TableRow>,
			);

			return (
				<PaddedDiv data-cy='premium-table'>
					<StyledTitleBolded>Premium</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							<TableBody>{premiumRows}</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		} else {
			return null;
		}
	};

	const renderFees = () => {
		if (fees && fees.length > 0) {
			const feeRows = fees.map((fee, index) => {
				const { name: feeName, amount } = fee;
				const feeTitle = `Fee ${index + 1}: ${feeName}`;
				return (
					<TableRow key={`fee-${index}`}>
						<TableCell>{feeTitle}</TableCell>
						<TableCell align='right'>
							{formatCurrency(parseFloat(amount), currency)}
						</TableCell>
					</TableRow>
				);
			});

			feeRows.push(
				<TableRow key='fee-total'>
					<TableCell>Total Fees</TableCell>
					<TableCell align='right'>
						{formatCurrency(totalFees, currency)}
					</TableCell>
				</TableRow>,
			);

			return (
				<PaddedDiv data-cy='fees-table'>
					<StyledTitleBolded>Fees</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							<TableBody>{feeRows}</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		} else {
			return null;
		}
	};

	const renderTaxes = () => {
		if (taxGroups && taxGroups.length > 0) {
			const taxRows = taxGroups.map((tax, index) => {
				const { name: taxName, amount } = tax;
				const taxTitle = `Tax ${index + 1}: ${taxName}`;
				return (
					<TableRow key={`tax-${index}`}>
						<TableCell>{taxTitle}</TableCell>
						<TableCell align='right'>
							{formatCurrency(parseFloat(amount), currency)}
						</TableCell>
					</TableRow>
				);
			});
			taxRows.push(
				<TableRow key='taxes-total'>
					<TableCell>Total Taxes</TableCell>
					<TableCell align='right'>
						{formatCurrency(totalTaxes, currency)}
					</TableCell>
				</TableRow>,
			);

			return (
				<PaddedDiv data-cy='taxes-table'>
					<StyledTitleBolded>Taxes</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							<TableBody>{taxRows}</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		} else {
			return null;
		}
	};

	const renderCommissions = () => {
		if (commissions && commissions.length > 0) {
			const commissionRows = commissions.map((commission, index) => {
				const { recipient, amount } = commission;
				const commissionTitle = `Recipient ${index + 1}: ${recipient}`;

				return (
					<TableRow key={`commission-${index}`}>
						<TableCell>{commissionTitle}</TableCell>
						<TableCell align='right'>
							{formatCurrency(parseFloat(amount), currency)}
						</TableCell>
					</TableRow>
				);
			});

			commissionRows.push(
				<TableRow key='commission-total'>
					<TableCell>Total Commissions</TableCell>
					<TableCell align='right'>
						{formatCurrency(totalCommissions, currency)}
					</TableCell>
				</TableRow>,
			);

			return (
				<PaddedDiv data-cy={'commissions-table'}>
					<StyledTitleBolded>Commissions</StyledTitleBolded>
					<TableContainer>
						<Table size='small'>
							<TableBody>{commissionRows}</TableBody>
						</Table>
					</TableContainer>
				</PaddedDiv>
			);
		} else {
			return null;
		}
	};

	const renderSummaryTable = () => {
		return (
			<>
				<StyledTitleBolded>Summary</StyledTitleBolded>
				<TableContainer>
					<Table size='small'>
						<TableBody>
							<TableRow>
								<TableCell>Gross Premium</TableCell>
								<TableCell align='right'>
									{formatCurrency(totalPremium, currency)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Fees</TableCell>
								<TableCell align='right'>
									{formatCurrency(totalFees, currency)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Taxes</TableCell>
								<TableCell align='right'>
									{formatCurrency(totalTaxes, currency)}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Total Due</TableCell>
								<TableCell align='right'>
									{formatCurrency(totalDue, currency)}
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</>
		);
	};

	return (
		<>
			{renderSummaryTable()}
			{renderPremiums()}
			{renderFees()}
			{renderTaxes()}
			{renderCommissions()}
		</>
	);
}

export default withPolicyContext<Props>(PricePreviewTables);
